/** Settings → Account → two-factor authentication. Recovery codes stay in memory and are never fetched. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import qrcode from "qrcode-generator";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import {
  Alert,
  Button,
  ErrorAlert,
  Field,
  Form,
  Input,
  Modal,
  PasswordInput,
  buttonClass,
} from "@/components/ui";
import { identity } from "@/lib/api";
import type { Account, MfaEnrollment } from "@/lib/types";

type View = "home" | "confirm" | "codes" | "regenerate" | "disable" | "disabled";

export function MfaModal({ account, onClose }: { account: Account; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>("home");
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // The account prop only changes once /v1/me is refetched; this keeps the modal right in the meantime.
  const [enabledOverride, setEnabledOverride] = useState<boolean | null>(null);
  const enabled = enabledOverride ?? account.mfa_enabled;
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const firstView = useRef(true);

  // Each step replaces the previous controls, so move focus to the new step (named by the dialog title).
  useEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    bodyRef.current?.focus();
  }, [view]);

  const begin = useMutation({
    mutationFn: identity.beginMfa,
    onSuccess: (next) => {
      setEnrollment(next);
      setCode("");
      setView("confirm");
    },
  });
  const activate = useMutation({
    mutationFn: () => identity.activateMfa(code.trim()),
    onSuccess: (next) => {
      setEnabledOverride(true);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      showCodes(next.recovery_codes);
    },
  });
  const regenerate = useMutation({
    mutationFn: () => identity.regenerateMfaRecoveryCodes(code.trim()),
    onSuccess: (next) => showCodes(next.recovery_codes),
  });
  const disable = useMutation({
    mutationFn: () =>
      identity.disableMfa({
        ...(password.trim() ? { password } : {}),
        ...(code.trim() ? { code: code.trim() } : {}),
      }),
    onSuccess: () => {
      setEnabledOverride(false);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setView("disabled");
    },
  });

  function showCodes(codes: string[]) {
    setRecoveryCodes(codes);
    setCopied(false);
    setCopyError(false);
    setDownloaded(false);
    setConfirmDiscard(false);
    setCode("");
    setPassword("");
    setView("codes");
  }

  function startSetup() {
    begin.reset();
    begin.mutate();
  }

  function goHome() {
    begin.reset();
    activate.reset();
    regenerate.reset();
    disable.reset();
    setEnrollment(null);
    setCode("");
    setPassword("");
    setView("home");
  }

  /** The codes exist only here: closing before they were copied or downloaded asks first. */
  function requestClose() {
    if (view === "codes" && !copied && !downloaded) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  function submitCode(event: React.FormEvent<HTMLFormElement>, action: () => void) {
    event.preventDefault();
    if (code.trim().length >= 6) action();
  }

  function submitDisable(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password.trim() || code.trim()) disable.mutate();
  }

  const error = begin.error ?? activate.error ?? regenerate.error ?? disable.error;
  const liveMessage = begin.isPending
    ? "Preparing setup…"
    : activate.isPending
      ? "Confirming your code…"
      : regenerate.isPending
        ? "Regenerating recovery codes…"
        : disable.isPending
          ? "Disabling two-factor authentication…"
          : copied
            ? "Recovery codes copied."
            : downloaded
              ? "Recovery codes downloaded."
              : "";
  const title =
    view === "confirm"
      ? "Confirm your authenticator"
      : view === "codes"
        ? "Save your recovery codes"
        : view === "regenerate"
          ? "Regenerate recovery codes"
          : view === "disable"
            ? "Disable two-factor authentication"
            : view === "disabled"
              ? "Two-factor authentication disabled"
              : "Two-factor authentication";

  return (
    <Modal
      open
      onClose={requestClose}
      title={title}
      titleId={titleId}
      size="lg"
      footer={
        view === "disabled" ? (
          <Button unstyled type="button" className={buttonClass("brand", "lg")} onClick={onClose}>
            Done
          </Button>
        ) : view === "codes" ? (
          <Button unstyled type="button" className={buttonClass("brand", "lg")} onClick={onClose}>
            I saved them
          </Button>
        ) : view === "home" ? (
          <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={goHome}>
            Back
          </Button>
        )
      }
    >
      <div
        ref={bodyRef}
        role="group"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="space-y-4 outline-none"
      >
        <p role="status" className="sr-only">
          {liveMessage}
        </p>
        {view === "home" && (
          <HomeView
            enabled={enabled}
            starting={begin.isPending}
            onStart={startSetup}
            onRegenerate={() => {
              regenerate.reset();
              setCode("");
              setView("regenerate");
            }}
            onDisable={() => {
              disable.reset();
              setCode("");
              setPassword("");
              setView("disable");
            }}
          />
        )}

        {view === "confirm" && enrollment && (
          <ConfirmView
            enrollment={enrollment}
            code={code}
            pending={activate.isPending}
            error={activate.error}
            onCodeChange={setCode}
            onSubmit={(event) => submitCode(event, () => activate.mutate())}
          />
        )}

        {view === "codes" && recoveryCodes && (
          <CodesView
            codes={recoveryCodes}
            copied={copied}
            copyError={copyError}
            downloaded={downloaded}
            confirmDiscard={confirmDiscard}
            onCopy={() => {
              setCopyError(false);
              void copyText(recoveryCodes.join("\n"))
                .then(() => setCopied(true))
                .catch(() => setCopyError(true));
            }}
            onDownload={() => {
              downloadText(recoveryCodes.join("\n"), "cortexgrip-recovery-codes.txt");
              setDownloaded(true);
            }}
            onKeep={() => setConfirmDiscard(false)}
            onDiscard={onClose}
          />
        )}

        {view === "regenerate" && (
          <StepUpCodeView
            code={code}
            pending={regenerate.isPending}
            error={regenerate.error}
            onCodeChange={setCode}
            onSubmit={(event) => submitCode(event, () => regenerate.mutate())}
            submitLabel="Regenerate codes"
          />
        )}

        {view === "disable" && (
          <DisableView
            code={code}
            password={password}
            pending={disable.isPending}
            error={disable.error}
            onCodeChange={setCode}
            onPasswordChange={setPassword}
            onSubmit={submitDisable}
          />
        )}

        {view === "disabled" && (
          <Alert tone="success">
            Two-factor authentication is off. Your current sessions remain signed in; set it up again before
            using security-sensitive actions.
          </Alert>
        )}

        {view === "home" && <ErrorAlert error={error} />}
      </div>
    </Modal>
  );
}

/** What the code fields say when the format is wrong. */
const CODE_FORMAT = "Enter the 6-digit code from your authenticator app";

function HomeView({
  enabled,
  starting,
  onStart,
  onRegenerate,
  onDisable,
}: {
  enabled: boolean;
  starting: boolean;
  onStart: () => void;
  onRegenerate: () => void;
  onDisable: () => void;
}) {
  return enabled ? (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-fg-muted">
        Your authenticator app protects sign-in and security-sensitive actions. Recovery codes are single-use
        backups; keep them somewhere safe.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onRegenerate}>
          Regenerate recovery codes
        </Button>
        <Button unstyled type="button" className={buttonClass("dangerOutline", "lg")} onClick={onDisable}>
          Disable MFA
        </Button>
      </div>
    </div>
  ) : (
    <div className="space-y-4">
      <div className="space-y-2 text-sm leading-relaxed text-fg-muted">
        <p>Use an authenticator app to add a second step when you sign in.</p>
        <p>You will receive recovery codes after confirming the first code. They are shown once only.</p>
      </div>
      <Button
        unstyled
        type="button"
        className={buttonClass("brand", "lg")}
        onClick={onStart}
        disabled={starting}
        aria-busy={starting}
      >
        {starting ? "Preparing setup…" : "Set up MFA"}
      </Button>
    </div>
  );
}

function ConfirmView({
  enrollment,
  code,
  pending,
  error,
  onCodeChange,
  onSubmit,
}: {
  enrollment: MfaEnrollment;
  code: string;
  pending: boolean;
  error: unknown;
  onCodeChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  const svg = useQrSvg(enrollment.provisioning_uri);
  return (
    <Form className="space-y-4" onSubmit={onSubmit} error={error}>
      <p className="text-sm leading-relaxed text-fg-muted">
        Scan this QR code in your authenticator app, or enter the manual setup key. Then enter the code it
        generates to confirm setup.
      </p>
      <div className="grid gap-5 sm:grid-cols-[160px_1fr] sm:items-center">
        <div
          className="mx-auto flex size-40 items-center justify-center overflow-hidden rounded-xl border border-border bg-white p-2 [&>svg]:size-full"
          role="img"
          aria-label="Authenticator QR code. A text alternative is provided by the manual setup key below."
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        <div className="space-y-2">
          <p className="text-xs font-semibold tracking-wide text-fg-subtle uppercase">Manual setup key</p>
          <code className="block break-all rounded-lg bg-muted px-3 py-2 text-sm font-semibold tracking-[0.12em] text-fg">
            {enrollment.secret}
          </code>
          <CopyButton value={enrollment.secret} label="Copy setup key" />
          <p className="text-xs leading-relaxed text-fg-subtle">
            This text is the accessible alternative to scanning the QR code.
          </p>
        </div>
      </div>
      <ErrorAlert error={error} />
      <Field
        label="Authenticator code"
        name="code"
        required
        hint="Use the current six-digit code from your app."
      >
        {(p) => (
          <Input
            {...p}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6,12}"
            title={CODE_FORMAT}
            maxLength={12}
            value={code}
            onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, ""))}
          />
        )}
      </Field>
      <Button
        type="submit"
        variant="brand"
        size="lg"
        loading={pending}
        disabledReason={code.trim().length < 6 ? "Enter the code from your authenticator app." : undefined}
      >
        {pending ? "Confirming…" : "Confirm and show recovery codes"}
      </Button>
    </Form>
  );
}

function StepUpCodeView({
  code,
  pending,
  error,
  onCodeChange,
  onSubmit,
  submitLabel,
}: {
  code: string;
  pending: boolean;
  error: unknown;
  onCodeChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitLabel: string;
}) {
  return (
    <Form className="space-y-4" onSubmit={onSubmit} error={error}>
      <p className="text-sm leading-relaxed text-fg-muted">
        Enter a current code from your authenticator app to prove it is you. Your previous recovery codes will
        stop working.
      </p>
      <ErrorAlert error={error} />
      <Field label="Current authenticator code" name="code" required>
        {(p) => (
          <Input
            {...p}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6,12}"
            title={CODE_FORMAT}
            maxLength={12}
            value={code}
            onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, ""))}
          />
        )}
      </Field>
      <Button
        type="submit"
        variant="brand"
        size="lg"
        loading={pending}
        disabledReason={code.trim().length < 6 ? "Enter the code from your authenticator app." : undefined}
      >
        {pending ? "Working…" : submitLabel}
      </Button>
    </Form>
  );
}

function DisableView({
  code,
  password,
  pending,
  error,
  onCodeChange,
  onPasswordChange,
  onSubmit,
}: {
  code: string;
  password: string;
  pending: boolean;
  error: unknown;
  onCodeChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <Form
      className="space-y-4"
      onSubmit={onSubmit}
      error={error}
      // either proof will do, so neither field is `required` on its own
      validate={() => ({
        password: !password.trim() && !code.trim() && "Enter your password or a current authenticator code.",
      })}
    >
      <p className="text-sm leading-relaxed text-fg-muted">
        Disabling MFA lowers account protection. Confirm with either your current authenticator code or your
        password. You can set MFA up again at any time.
      </p>
      <ErrorAlert error={error} />
      <Field label="Password" name="password" hint="Use this or the authenticator code below.">
        {(p) => (
          <PasswordInput
            {...p}
            autoComplete="current-password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
          />
        )}
      </Field>
      <Field label="Current authenticator code" name="code">
        {(p) => (
          <Input
            {...p}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6,12}"
            title={CODE_FORMAT}
            maxLength={12}
            value={code}
            onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, ""))}
          />
        )}
      </Field>
      <Button
        unstyled
        type="submit"
        className={buttonClass("danger", "lg")}
        disabled={pending}
        aria-busy={pending}
      >
        {pending ? "Disabling…" : "Disable MFA"}
      </Button>
    </Form>
  );
}

function CodesView({
  codes,
  copied,
  copyError,
  downloaded,
  confirmDiscard,
  onCopy,
  onDownload,
  onKeep,
  onDiscard,
}: {
  codes: string[];
  copied: boolean;
  copyError: boolean;
  downloaded: boolean;
  confirmDiscard: boolean;
  onCopy: () => void;
  onDownload: () => void;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="space-y-4">
      <Alert tone="warning">
        Save these codes now. Each code works once, and CortexGrip will not show them again after you close
        this window.
      </Alert>
      <ol
        aria-label="Recovery codes"
        className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-muted p-4 font-mono text-sm font-semibold text-fg sm:grid-cols-3"
      >
        {codes.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onCopy}>
          {copied ? "Copied" : "Copy all codes"}
        </Button>
        <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onDownload}>
          {downloaded ? "Downloaded" : "Download .txt"}
        </Button>
      </div>
      {copyError && (
        <p role="alert" className="text-xs text-red-600">
          Copy failed. Use the download button instead.
        </p>
      )}
      {confirmDiscard && (
        <Alert tone="danger">
          <p className="font-medium">You haven&apos;t copied or downloaded these codes.</p>
          <p className="mt-1">
            If you close now they are gone for good. You would have to regenerate new ones.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onKeep}>
              Keep viewing
            </Button>
            <Button unstyled type="button" className={buttonClass("dangerOutline", "lg")} onClick={onDiscard}>
              Discard codes
            </Button>
          </div>
        </Alert>
      )}
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div>
      <Button
        unstyled
        type="button"
        className={buttonClass("secondary", "lg")}
        onClick={() => {
          setFailed(false);
          void copyText(value)
            .then(() => setCopied(true))
            .catch(() => setFailed(true));
        }}
      >
        {copied ? "Copied" : label}
      </Button>
      <p role="status" className="sr-only">
        {copied ? "Setup key copied." : ""}
      </p>
      {failed && (
        <p role="alert" className="mt-1 text-xs text-red-600">
          Copy failed. Select the key manually.
        </p>
      )}
    </div>
  );
}

function useQrSvg(uri: string): string {
  return useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(uri, "Byte");
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 4, scalable: true });
  }, [uri]);
}

async function copyText(value: string): Promise<void> {
  if (!navigator.clipboard) throw new Error("Clipboard unavailable");
  await navigator.clipboard.writeText(value);
}

function downloadText(value: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([value + "\n"], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
